/**
 * Display-value lookups for system messages — the notices Lotus staff target at users
 * inside accounts.
 *
 * Every coded value the API returns for a message (severity, audience, status) is mapped
 * here rather than decided by an inline conditional in a template. The API will grow codes
 * a template's ternary would silently mislabel, so each lookup is a map that extends in one
 * place plus a resolver that degrades gracefully on a code this build has never heard of.
 *
 * Entries carry a `Label` plus a semantic `Tone` — the tone maps to a CSS class, never a
 * raw colour (see assets/css2/tokens.css). Severity carries a second tone because the
 * banner and the pill speak different tone vocabularies: LTonePill takes
 * success|danger|pending|neutral|unconfirmed|interactive, while LBanner takes
 * info|warning|critical|maintenance|neutral.
 *
 * @see /helpers/display-values.js for the shared fallback rule.
 */

import { resolveDisplayValue } from "/helpers/display-values.js";




//---------------------------------------------------------------------------
// Severity — how serious the message is
//---------------------------------------------------------------------------

export const systemMessageSeverities = {
	Info: { Label: "Info", Tone: "unconfirmed", BannerTone: "info", Icon: "circle-info" },
	Warning: { Label: "Warning", Tone: "pending", BannerTone: "warning", Icon: "triangle-exclamation" },
	Critical: { Label: "Critical", Tone: "danger", BannerTone: "critical", Icon: "circle-exclamation" },
	Maintenance: { Label: "Maintenance", Tone: "neutral", BannerTone: "maintenance", Icon: "screwdriver-wrench" },
};


/**
 * Resolve a severity code.
 *
 * An unmapped code shows as the raw code on a neutral banner rather than rendering blank
 * or borrowing another severity's colour — a new severity from the API has to be visible,
 * not silently dressed as an Info notice.
 */
export function resolveSystemMessageSeverity(code)
{
	const entry = systemMessageSeverities[code];
	if (entry) return entry;

	return {
		...resolveDisplayValue(systemMessageSeverities, code),
		BannerTone: "neutral",
		Icon: "circle-info",
	};
}




//---------------------------------------------------------------------------
// Audience — who inside the targeted account(s) sees the message
//---------------------------------------------------------------------------

export const systemMessageAudiences = {
	AllUsers: { Label: "Everyone", Tone: "neutral", Description: "Every user in the targeted accounts." },
	AccountAdministrators: { Label: "Account administrators", Tone: "interactive", Description: "Users who administer their account." },
	TimesheetApprovers: { Label: "Timesheet approvers", Tone: "interactive", Description: "Users who approve other people's time." },
	SpecificUsers: { Label: "Specific people", Tone: "unconfirmed", Description: "Only the people you name." },
};


export function resolveSystemMessageAudience(code)
{
	return resolveDisplayValue(systemMessageAudiences, code);
}




//---------------------------------------------------------------------------
// Status — where the message is in its own lifecycle
//---------------------------------------------------------------------------

export const systemMessageStatuses = {
	Draft: { Label: "Draft", Tone: "neutral", Description: "Being composed. Shows to nobody." },
	Published: { Label: "Published", Tone: "success", Description: "Live, within its publication window." },
	Archived: { Label: "Withdrawn", Tone: "danger", Description: "Stopped. Still readable in message history." },
};


export function resolveSystemMessageStatus(code)
{
	return resolveDisplayValue(systemMessageStatuses, code);
}




//---------------------------------------------------------------------------
// Reader-facing state — where one person stands with one message
//---------------------------------------------------------------------------

export const systemMessageReaderStates = {
	Showing: { Label: "Showing", Tone: "unconfirmed" },
	Dismissed: { Label: "Dismissed", Tone: "neutral" },
	Ended: { Label: "Ended", Tone: "neutral" },
	Withdrawn: { Label: "Withdrawn", Tone: "neutral" },
};


export function resolveSystemMessageReaderState(code)
{
	return resolveDisplayValue(systemMessageReaderStates, code);
}


/**
 * Which reader state one message is in, for this reader.
 *
 * The derivation lives here rather than in the Messages page's markup — the page renders
 * `resolveSystemMessageReaderState(getReaderStateCode(m))` and decides nothing.
 *
 * Note the API deliberately doesn't tell a reader a message's `Status`: whether Lotus
 * withdrew a notice is internal. So "ended" and "withdrawn" are told apart by the only
 * evidence the reader legitimately has — a message that is no longer live and had an end
 * date simply ran its course; one with no end date must have been stopped.
 */
export function getReaderStateCode(message)
{
	if (message == null) return "";

	if (message.IsDismissed) return "Dismissed";

	if (message.IsCurrentlyActive) return "Showing";

	if (message.PublishUntilUTC) return "Ended";

	return "Withdrawn";
}




//---------------------------------------------------------------------------
// Select options built from the maps above
//---------------------------------------------------------------------------

/**
 * `[{ value, label }]` options for an LSelect, from one of the maps above.
 *
 * @param {object} map One of the display maps in this module.
 * @param {string} allLabel When set, prepends a leading "" option with this label — for a
 * filter where "everything" is a real choice rather than LSelect's disabled placeholder.
 */
export function selectOptionsFromMap(map, allLabel = "")
{
	const options = Object.keys(map).map((code) => ({ value: code, label: map[code].Label }));

	if (allLabel) options.unshift({ value: "", label: allLabel });

	return options;
}
